import '@ant-design/v5-patch-for-react-19';
import React, { type ReactNode } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, App } from 'antd';
import { createRoot } from 'react-dom/client';

import OperatorApp from './components/operator/OperatorApp';
import { OperatorAuthProvider, useOperatorAuth } from './contexts/operator/OperatorAuthContext';
import { OperatorDataProvider } from './contexts/operator/OperatorDataContext';
import { OperatorMenuProvider } from './contexts/operator/OperatorMenuContext';
import { OperatorNotificationProvider } from './contexts/operator/OperatorNotificationContext';
import { PlanProvider as PlanProviderImpl } from './contexts/operator/PlanContext';
import { StorageModeProvider as StorageModeProviderImpl } from './contexts/operator/StorageModeContext';
import { initI18n } from './i18n/i18n.js';
import './styles/operator.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

interface ProviderProps {
  children: ReactNode;
}

// 橋接 OperatorAuth → PlanProvider
const OperatorPlanProvider: React.FC<ProviderProps> = ({ children }) => {
  const { operator } = useOperatorAuth();
  // PlanProvider 仍為 .jsx，TS 從 destructure 推不出 React.FC 簽名，
  // 用 ComponentType<any> 包一層保留 runtime 行為（cast 放 body 內避免 HMR TDZ）。
  const PlanProvider = PlanProviderImpl as React.ComponentType<any>;
  return (
    <PlanProvider plan={operator?.plan} enabledFeatures={operator?.enabled_features}>
      {children}
    </PlanProvider>
  );
};

// 橋接 OperatorAuth → StorageModeProvider
const OperatorStorageModeProvider: React.FC<ProviderProps> = ({ children }) => {
  const { operator } = useOperatorAuth();
  // OperatorAuthContext 把 warehouse 標成 unknown（schema 尚未細化），
  // 這裡保留原 .jsx 的 optional-chain 取值行為，僅做最小 cast。
  const warehouse = operator?.warehouse as { storage_mode?: unknown } | undefined;
  const StorageModeProvider = StorageModeProviderImpl as React.ComponentType<any>;
  return (
    <StorageModeProvider mode={warehouse?.storage_mode}>
      {children}
    </StorageModeProvider>
  );
};

// 確保DOM加載完成後再渲染
document.addEventListener('DOMContentLoaded', async () => {
  const container = document.getElementById('operator-root');
  if (container) {
    await initI18n();
    document.body.classList.add('operator-theme');
    const root = createRoot(container);

    root.render(
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: ' #4682B4',
            fontSize: 16,
          },
        }}
      >
        <App>
          <QueryClientProvider client={queryClient}>
            <OperatorAuthProvider>
              <OperatorPlanProvider>
                <OperatorStorageModeProvider>
                  <OperatorNotificationProvider>
                    <OperatorMenuProvider>
                      <OperatorDataProvider>
                        <OperatorApp />
                      </OperatorDataProvider>
                    </OperatorMenuProvider>
                  </OperatorNotificationProvider>
                </OperatorStorageModeProvider>
              </OperatorPlanProvider>
            </OperatorAuthProvider>
          </QueryClientProvider>
        </App>
      </ConfigProvider>
    );
  }
});
